/**
 * The colour a seal wears as its moment approaches.
 *
 * This is not decoration. With up to fifty mandalas on screen, the player
 * cannot read six rings of Devanagari on each one to work out which is due
 * next — but they can see hue in peripheral vision without looking at it. So
 * the whole figure becomes a clock face: blue is far off, green is closing, red
 * is near, orange means the window is open, pale gold is the moment itself.
 *
 * Every threshold lives here, in one ordered table, and the shader is handed
 * these same numbers as uniforms. There are no colour constants in the GLSL.
 *
 * ## Why two tables
 *
 * The approach and the overrun are deliberately not mirror images. A symmetric
 * ramp would make "300 ms early" and "300 ms late" look identical, and those
 * are opposite mistakes needing opposite corrections. The approach warms
 * through the spectrum; the overrun falls out of it into violet, so *passed*
 * never reads as *coming*.
 */

export interface TimingStop {
  /** Position on the ramp: 0..1 for the approach, seconds for the overrun. */
  readonly at: number;
  readonly rgb: readonly [number, number, number];
}

/**
 * The approach, sampled by "heat" — see `timelineHeat`.
 *
 * Seven stops rather than the four the eye is meant to name. Green to red in a
 * straight RGB line passes through olive, which reads as a dirty green rather
 * than as a warning; the amber stop at 0.48 puts the path through a colour
 * worth seeing. The cyan stop does the same job between blue and green.
 */
export const APPROACH_STOPS: readonly TimingStop[] = [
  { at: 0.0, rgb: [0.16, 0.4, 1.0] }, // blue — just appeared
  { at: 0.15, rgb: [0.16, 0.86, 1.0] }, // cyan
  { at: 0.3, rgb: [0.24, 1.0, 0.52] }, // green — closing
  { at: 0.48, rgb: [0.95, 0.94, 0.22] }, // amber
  { at: 0.65, rgb: [1.0, 0.28, 0.2] }, // red — near
  { at: 0.88, rgb: [1.0, 0.63, 0.16] }, // orange — window open
  { at: 1.0, rgb: [1.0, 0.94, 0.78] }, // pale gold — now
];

/** The overrun, sampled in seconds past the best moment. */
export const OVERRUN_STOPS: readonly TimingStop[] = [
  { at: 0.0, rgb: [1.0, 0.94, 0.78] }, // pale gold
  { at: 0.12, rgb: [1.0, 0.6, 0.18] }, // orange — still GREAT
  { at: 0.2, rgb: [1.0, 0.22, 0.42] }, // magenta-red — edge of GOOD
  { at: 0.5, rgb: [0.42, 0.14, 0.62] }, // violet — gone
  { at: 1.2, rgb: [0.2, 0.06, 0.3] }, // deep violet
];

/**
 * Where the approach ramp is cut over from progress to the judge.
 *
 * Below this the colour follows visual progress, so the ramp fills whatever
 * lead the wave speed gave it. Above it the colour follows the *judgement
 * window* instead, so "orange" means the GOOD window is open and "pale gold"
 * means PERFECT — at every wave speed, on every chart. Without the handover,
 * 0.5× would show orange half a second before the window and 2× would show it
 * after, and the colour would stop being a promise.
 */
export const HANDOVER_HEAT = 0.88;

export interface TimingColorTimeline {
  readonly approach: readonly TimingStop[];
  readonly overrun: readonly TimingStop[];
  readonly handoverHeat: number;
}

export const DEFAULT_TIMING_TIMELINE: TimingColorTimeline = Object.freeze({
  approach: APPROACH_STOPS,
  overrun: OVERRUN_STOPS,
  handoverHeat: HANDOVER_HEAT,
});

/**
 * How far along the ramp a wave is, 0 to 1.
 *
 * Progress drives it until `handoverHeat`; from there the distance to the best
 * moment does, measured against the GOOD window. The two agree closely at 1×
 * by construction — a lead of 1.75 s puts progress 0.88 at 210 ms out, and the
 * GOOD window is 200 ms — and the handover is what keeps them agreeing when the
 * lead changes.
 */
export function timelineHeat(
  progress: number,
  timingOffsetSeconds: number,
  goodSeconds: number,
  timeline: TimingColorTimeline = DEFAULT_TIMING_TIMELINE,
): number {
  const handover = timeline.handoverHeat;
  const lead = leadFrom(progress, timingOffsetSeconds);
  // The outer stretch is normalised so that it *finishes* at the edge of the
  // GOOD window rather than at the best moment. That is the whole trick: the
  // blue-to-orange ramp stretches to fill whatever lead the player chose, but
  // its top end is pinned to a judging threshold, so "orange" means the window
  // is open at 0.5x and at 2x alike. Without the normalisation a short lead
  // reaches orange late and a long one reaches it early.
  const outerSpan = Math.max(lead - goodSeconds, 1e-4);
  const outer = clamp01((lead + timingOffsetSeconds) / outerSpan);
  const nearness = 1 - clamp01(Math.abs(timingOffsetSeconds) / Math.max(goodSeconds, 1e-4));
  return Math.min(1, handover * outer + (1 - handover) * nearness);
}

/** Samples an ordered stop table. Shared by the CPU mirror and the tests. */
export function sampleStops(
  stops: readonly TimingStop[],
  at: number,
): [number, number, number] {
  const first = stops[0] as TimingStop;
  if (at <= first.at) return [...first.rgb] as [number, number, number];
  for (let index = 1; index < stops.length; index += 1) {
    const previous = stops[index - 1] as TimingStop;
    const current = stops[index] as TimingStop;
    if (at > current.at) continue;
    const span = current.at - previous.at;
    const t = span <= 0 ? 0 : (at - previous.at) / span;
    return [
      previous.rgb[0] + (current.rgb[0] - previous.rgb[0]) * t,
      previous.rgb[1] + (current.rgb[1] - previous.rgb[1]) * t,
      previous.rgb[2] + (current.rgb[2] - previous.rgb[2]) * t,
    ];
  }
  const last = stops[stops.length - 1] as TimingStop;
  return [...last.rgb] as [number, number, number];
}

/**
 * The timeline colour for a wave, exactly as the shader computes it.
 *
 * Kept in step with the GLSL by being the thing the tests check; the shader is
 * handed the same tables as uniforms, so the two cannot use different palettes,
 * only different arithmetic — and that is what the tests are for.
 */
export function timelineColor(
  progress: number,
  timingOffsetSeconds: number,
  goodSeconds: number,
  timeline: TimingColorTimeline = DEFAULT_TIMING_TIMELINE,
): [number, number, number] {
  if (timingOffsetSeconds > 0) {
    return sampleStops(timeline.overrun, timingOffsetSeconds);
  }
  return sampleStops(
    timeline.approach,
    timelineHeat(progress, timingOffsetSeconds, goodSeconds, timeline),
  );
}

/** Flattens a stop table into the two arrays the shader takes as uniforms. */
export function stopUniforms(stops: readonly TimingStop[]): {
  positions: number[];
  colors: number[];
} {
  return {
    positions: stops.map((stop) => stop.at),
    colors: stops.flatMap((stop) => [...stop.rgb]),
  };
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/**
 * Recovers the wave's display lead from the pair the caller already has.
 *
 * `progress = 1 + offset / lead`, so `lead = -offset / (1 - progress)`. The
 * shader is handed the lead directly and does not need this; the CPU mirror
 * takes the same two arguments the shader has in its varyings, so that the two
 * are demonstrably computing from the same inputs.
 */
function leadFrom(progress: number, timingOffsetSeconds: number): number {
  const remaining = 1 - clamp01(progress);
  if (remaining <= 1e-6) return Math.max(Math.abs(timingOffsetSeconds), 1e-3);
  return Math.max(-timingOffsetSeconds / remaining, 1e-3);
}
